import React, { useMemo, useState } from 'react';
import { FiZoomOut as ZoomOut, FiRotateCcw as RotateCcw, FiDownload as Download } from 'react-icons/fi';
import { BLUE, BLUE_DARK, PURPLE, PURPLE_DARK, GREEN, AMBER, GRAY, GRAY_DARK } from '../../../../../constants/chartColors';

/**
 * FlamegraphComponent - Interactive flamegraph visualization for agent run traces
 *
 * Data structure expected:
 * {
 *   name: string,
 *   value: number (duration in ms),
 *   children: [...],
 *   type: 'llm' | 'tool' | 'memory' | 'reasoning' | 'other',
 *   metadata: { ... }
 * }
 */

interface SpanColorEntry {
  bg: string;
  hover: string;
  text: string;
}

const SPAN_COLORS: Record<string, SpanColorEntry> = {
  llm: { bg: BLUE, hover: BLUE_DARK, text: '#fff' },
  tool: { bg: PURPLE, hover: PURPLE_DARK, text: '#fff' },
  memory: { bg: GREEN, hover: GREEN, text: '#fff' },
  reasoning: { bg: AMBER, hover: '#D97706', text: '#fff' },
  generation: { bg: BLUE, hover: BLUE_DARK, text: '#fff' },
  action: { bg: PURPLE, hover: PURPLE_DARK, text: '#fff' },
  observation: { bg: GREEN, hover: GREEN, text: '#fff' },
  other: { bg: GRAY, hover: GRAY_DARK, text: '#fff' },
};

const getSpanColor = (type: string | undefined): SpanColorEntry => {
  const normalizedType = type?.toLowerCase() ?? 'other';
  return SPAN_COLORS[normalizedType] ?? SPAN_COLORS.other;
};

interface StepData {
  id?: string;
  step_number?: number;
  tool_used?: string;
  action?: string;
  duration_seconds?: number;
  input_tokens?: number;
  output_tokens?: number;
  cost?: number;
  status?: string;
}

// Derive step type from tool_used or action
const deriveStepType = (step: StepData): string => {
  const toolUsed = step.tool_used?.toLowerCase() ?? '';
  const action = step.action?.toLowerCase() ?? '';

  if (toolUsed) {
    if (toolUsed.includes('llm') || toolUsed.includes('generate') || toolUsed.includes('chat') || toolUsed.includes('complete')) {
      return 'llm';
    }
    if (toolUsed.includes('memory') || toolUsed.includes('retrieve') || toolUsed.includes('store')) {
      return 'memory';
    }
    if (toolUsed.includes('reason') || toolUsed.includes('think') || toolUsed.includes('plan')) {
      return 'reasoning';
    }
    return 'tool';
  }

  if (action.includes('generate') || action.includes('llm') || action.includes('response')) {
    return 'llm';
  }
  if (action.includes('tool') || action.includes('execute') || action.includes('call')) {
    return 'tool';
  }
  if (action.includes('observe') || action.includes('result')) {
    return 'observation';
  }

  return 'action';
};

export interface FlameNode {
  name: string;
  value: number;
  type: string;
  metadata?: {
    step_number?: number;
    tool_used?: string | null;
    action?: string | null;
    input_tokens?: number;
    output_tokens?: number;
    cost?: number;
    status?: string;
  };
  children: FlameNode[];
}

// Transform steps data to flamegraph format
export const transformStepsToFlamegraph = (steps: StepData[], runDuration: number): FlameNode | null => {
  if (!steps || steps.length === 0) return null;

  const rootValue = runDuration * 1000 || steps.reduce((sum, s) => sum + (s.duration_seconds ?? 0) * 1000, 0);

  return {
    name: 'Agent Run',
    value: rootValue,
    type: 'other',
    children: steps.map((step, index) => {
      const stepName = step.action ?? step.tool_used ?? `Step ${step.step_number ?? index + 1}`;
      const stepType = deriveStepType(step);

      return {
        name: stepName,
        value: (step.duration_seconds ?? 0) * 1000,
        type: stepType,
        metadata: {
          step_number: step.step_number ?? index + 1,
          tool_used: step.tool_used,
          action: step.action,
          input_tokens: step.input_tokens,
          output_tokens: step.output_tokens,
          cost: step.cost,
          status: step.status,
        },
        children: [],
      };
    }),
  };
};

interface FlameBarProps {
  node: FlameNode;
  x: number;
  y: number;
  width: number;
  height: number;
  totalValue: number;
  onHover: (node: FlameNode | null) => void;
  onClick: (node: FlameNode) => void;
  isHovered: boolean;
  zoomLevel: number;
  minWidth?: number;
}

const FlameBar: React.FC<FlameBarProps> = ({
  node,
  x,
  y,
  width,
  height,
  totalValue: _totalValue,
  onHover,
  onClick,
  isHovered,
  minWidth = 2
}) => {
  const colors = getSpanColor(node.type);
  const displayWidth = Math.max(width, minWidth);

  if (displayWidth < 1) return null;

  const showLabel = displayWidth > 40;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={displayWidth}
        height={height}
        fill={isHovered ? colors.hover : colors.bg}
        stroke="#fff"
        strokeWidth={0.5}
        rx={2}
        style={{ cursor: 'pointer', transition: 'fill 0.15s ease' }}
        onMouseEnter={() => onHover(node)}
        onMouseLeave={() => onHover(null)}
        onClick={() => onClick(node)}
      />
      {showLabel && (
        <text
          x={x + displayWidth / 2}
          y={y + height / 2 + 4}
          fill={colors.text}
          fontSize={11}
          fontWeight={500}
          textAnchor="middle"
          style={{ pointerEvents: 'none' }}
        >
          {node.name.length > displayWidth / 8
            ? node.name.slice(0, Math.floor(displayWidth / 8)) + '...'
            : node.name
          }
        </text>
      )}
    </g>
  );
};

interface Props {
  data: FlameNode | null;
  width?: number;
  height?: number;
  onExport?: (type: string) => void;
}

const FlamegraphComponent: React.FC<Props> = ({
  data,
  width = 800,
  height = 400,
  onExport
}) => {
  const [hoveredNode, setHoveredNode] = useState<FlameNode | null>(null);
  const [zoomStack, setZoomStack] = useState<FlameNode[]>([]);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const BAR_HEIGHT = 24;
  const PADDING = 10;

  const currentRoot = useMemo<FlameNode | null>(() => {
    if (zoomStack.length === 0) return data;
    return zoomStack[zoomStack.length - 1];
  }, [data, zoomStack]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleZoomIn = (node: FlameNode): void => {
    if ((node.children?.length ?? 0) > 0) {
      setZoomStack([...zoomStack, node]);
    }
  };

  const handleZoomOut = (): void => {
    if (zoomStack.length > 0) {
      setZoomStack(zoomStack.slice(0, -1));
    }
  };

  const handleReset = (): void => {
    setZoomStack([]);
  };

  const handleExport = (): void => {
    if (onExport) {
      onExport('flamegraph');
    }
  };

  const renderFlameNodes = useMemo((): React.ReactNode[] => {
    if (!currentRoot) return [];

    const nodes: React.ReactNode[] = [];
    const chartWidth = width - PADDING * 2;
    const totalValue = currentRoot.value;

    const traverse = (node: FlameNode, depth: number, startX: number): void => {
      const nodeWidth = (node.value / totalValue) * chartWidth;
      const y = PADDING + depth * (BAR_HEIGHT + 2);

      nodes.push(
        <FlameBar
          key={`${node.name}-${depth}-${startX}`}
          node={node}
          x={PADDING + startX}
          y={y}
          width={nodeWidth}
          height={BAR_HEIGHT}
          totalValue={totalValue}
          onHover={setHoveredNode}
          onClick={handleZoomIn}
          isHovered={hoveredNode?.name === node.name && hoveredNode?.value === node.value}
          zoomLevel={zoomStack.length}
        />
      );

      if ((node.children?.length ?? 0) > 0) {
        let childX = startX;
        node.children.forEach((child) => {
          const childWidth = (child.value / totalValue) * chartWidth;
          traverse(child, depth + 1, childX);
          childX += childWidth;
        });
      }
    };

    traverse(currentRoot, 0, 0);
    return nodes;
  }, [currentRoot, width, hoveredNode, zoomStack.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculateMaxDepth = (node: FlameNode, depth = 0): number => {
    if (!node.children || node.children.length === 0) return depth;
    return Math.max(...node.children.map(child => calculateMaxDepth(child, depth + 1)));
  };

  const maxDepth = currentRoot ? calculateMaxDepth(currentRoot) : 0;
  const svgHeight = Math.max(height, PADDING * 2 + (maxDepth + 1) * (BAR_HEIGHT + 2));

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <p className="text-gray-500 dark:text-gray-400">No data available for flamegraph</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={handleZoomOut}
            disabled={zoomStack.length === 0}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Zoom out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <button
            onClick={handleReset}
            disabled={zoomStack.length === 0}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Reset zoom"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          {zoomStack.length > 0 && currentRoot && (
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Viewing: {currentRoot.name}
            </span>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {/* Legend */}
          <div className="flex items-center space-x-3 text-xs">
            {Object.entries(SPAN_COLORS).slice(0, 4).map(([type, colors]) => (
              <div key={type} className="flex items-center">
                <div
                  className="w-3 h-3 rounded mr-1"
                  style={{ backgroundColor: colors.bg }}
                />
                <span className="capitalize text-gray-600 dark:text-gray-400">{type}</span>
              </div>
            ))}
          </div>

          <button
            onClick={handleExport}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
            title="Export as PNG"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* SVG Chart */}
      <div
        className="relative overflow-auto bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
        role="img"
        onMouseMove={handleMouseMove}
      >
        <svg width={width} height={svgHeight}>
          {renderFlameNodes}
        </svg>

        {/* Tooltip */}
        {hoveredNode && (
          <div
            className="absolute z-10 bg-white dark:bg-gray-800 shadow-lg rounded-lg p-3 border border-gray-200 dark:border-gray-700 pointer-events-none"
            style={{
              left: Math.min(tooltipPos.x + 10, width - 200),
              top: tooltipPos.y + 10,
              maxWidth: 250,
            }}
          >
            <div className="font-medium text-gray-900 dark:text-white mb-1">
              {hoveredNode.name}
            </div>
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Duration:</span>
                <span className="text-gray-900 dark:text-white font-mono">
                  {hoveredNode.value >= 1000
                    ? `${(hoveredNode.value / 1000).toFixed(2)}s`
                    : `${hoveredNode.value.toFixed(1)}ms`
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Type:</span>
                <span className="text-gray-900 dark:text-white capitalize">
                  {hoveredNode.type}
                </span>
              </div>
              {hoveredNode.metadata && (
                <>
                  {hoveredNode.metadata.tool_used && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Tool:</span>
                      <span className="text-gray-900 dark:text-white">
                        {hoveredNode.metadata.tool_used}
                      </span>
                    </div>
                  )}
                  {hoveredNode.metadata.status && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Status:</span>
                      <span className={
                        hoveredNode.metadata.status === 'completed'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }>
                        {hoveredNode.metadata.status}
                      </span>
                    </div>
                  )}
                  {hoveredNode.metadata.input_tokens != null && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Tokens:</span>
                      <span className="text-gray-900 dark:text-white font-mono">
                        {hoveredNode.metadata.input_tokens}&darr; / {hoveredNode.metadata.output_tokens ?? 0}&uarr;
                      </span>
                    </div>
                  )}
                  {hoveredNode.metadata.cost != null && hoveredNode.metadata.cost > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Cost:</span>
                      <span className="text-gray-900 dark:text-white font-mono">
                        ${Number(hoveredNode.metadata.cost).toFixed(4)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
            {(hoveredNode.children?.length ?? 0) > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                Click to zoom into {hoveredNode.children.length} child spans
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlamegraphComponent;
